import { Injectable, signal, computed } from '@angular/core'
import { OktaAuth, AccessToken } from '@okta/okta-auth-js'
import { environment } from '../../environments/environment'
import { TRole, UserInfo } from '../models/user.model'
import { NotificationService } from './notification.service'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oktaAuth: OktaAuth

  private isAuthenticated = signal<boolean>(false)
  private userInfo = signal<UserInfo | null>(null)

  constructor(
    private router: Router,
    private notificationService: NotificationService,
  ) {
    this.oktaAuth = new OktaAuth({
      clientId: environment.okta.clientId,
      issuer: environment.okta.issuer,
      redirectUri: environment.okta.redirectUri,
      scopes: environment.okta.scopes,
      pkce: environment.okta.pkce,
      tokenManager: {
        storage: 'sessionStorage',
      },
    })

    this.checkAuthentication()
  }

  private async checkAuthentication(): Promise<void> {
    const authenticated = await this.oktaAuth.isAuthenticated()

    this.isAuthenticated.set(authenticated)

    const token = (await this.oktaAuth.tokenManager.get('accessToken')) as
      | AccessToken
      | undefined

    if (authenticated && token?.accessToken) {
      sessionStorage.setItem('okta_token', token.accessToken)
      await this.getUserInfo()
    }
  }

  async handleAuthentication(): Promise<void> {
    try {
      const tokenResponse = await this.oktaAuth.token.parseFromUrl()
      this.oktaAuth.tokenManager.setTokens(tokenResponse.tokens)

      await this.checkAuthentication()
    } catch (error) {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        'ERRORS.ERROR_OKTA_AUTH',
      )
    }
  }

  async login(): Promise<void> {
    try {
      await this.oktaAuth.signInWithRedirect()
    } catch (error) {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        'ERRORS.ERROR_CONNECTION',
      )
    }
  }

  async logout(): Promise<void> {
    try {
      this.oktaAuth.tokenManager.clear()
      sessionStorage.removeItem('okta_token')
      sessionStorage.removeItem('user_info')
      this.isAuthenticated.set(false)
      this.userInfo.set(null)
      this.router.navigate(['/login'])
    } catch (error) {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        'ERRORS.ERROR_DISCONNECTION',
      )
    }
  }

  private mapOktaRolesToAppRole(oktaRoles: string[] | null): UserInfo['role'] {
    if (oktaRoles) {
      if (oktaRoles.includes('APP_PORTABILITE_DPO')) {
        return 'admin'
      }
      if (oktaRoles.includes('APP_PORTABILITE_MANDATAIRE')) {
        return 'mandatary'
      }
    }
    return null
  }

  async getUserInfo(): Promise<UserInfo | null> {
    try {
      const storedUserInfo = sessionStorage.getItem('user_info')
      if (storedUserInfo) {
        const parsedInfo = JSON.parse(storedUserInfo)
        this.userInfo.set(parsedInfo)
        return parsedInfo
      }

      const oktaUser = await this.oktaAuth.getUser()

      const userInfo: UserInfo = {
        email: oktaUser.email || '',
        firstName: oktaUser.given_name || '',
        lastName: oktaUser.family_name || '',
        role: this.mapOktaRolesToAppRole(
          (oktaUser['appuser.groups'] as string[]) || 'mandatary',
        ),
      }
      sessionStorage.setItem('user_info', JSON.stringify(userInfo))
      this.userInfo.set(userInfo)
      return userInfo
    } catch (error) {
      this.notificationService.showToast(
        'error',
        'ERRORS.ERROR_TITLE',
        'ERRORS.ERROR_RETRIEVING_USER_INFO',
      )
      return null
    }
  }

  isLoggedIn = computed(() => this.isAuthenticated())
  currentUserInfo = computed(() => this.userInfo())
  currentRole = computed(() => this.currentUserInfo()?.role)

  hasRole(requiredRoles: TRole[]): boolean {
    const currentRole = this.currentRole()
    return requiredRoles.some((role) => currentRole === role)
  }

  async getAccessToken(): Promise<string | undefined> {
    return this.oktaAuth.getAccessToken()
  }

  getStoredToken(): string | null {
    return sessionStorage.getItem('okta_token')
  }
}
